import React, { useEffect, useRef, useState } from 'react';
import { webLoadingFalse, webNavigate } from '../../services/navigate.service';
import CloseIcon from '../../images/CloseIcon';
import {
  deleteDeviceUser,
  getListDeviceUser,
  updateDeviceUser,
} from '../../services/deviceUser.service';
import PlusIconWhite from '../../images/PlusIconWhite';
import EditIconV2 from '../../images/EditIconV2';
import DeleteIconV2 from '../../images/DeleteIconV2';
import { useResponsive } from '../../hook/useResponsive';
import DeviceUserIcon from '../../images/DeviceUserIcon';
import { showDeleteToast } from '../../services/toast.service';
import { showApiError } from '../../services/apiError';
import {
  ListColumn,
  ListQuery,
  ListRowAction,
  ListScreen,
  ListScreenHandle,
  Pill,
  cells,
  t,
} from '../../components/list';

interface DeviceUserRow {
  id: string;
  firstName: string;
  lastName: string;
  loginId: string;
  role: { roleName: string } | null;
  updatedAt: Date;
  statusId: number;
  isActive?: boolean;
}
interface DeviceUserResponse {
  data: {
    data: {
      deviceUser: DeviceUserRow[];
      count: number;
    };
  };
}

/** Active when statusId === 1, otherwise Suspended (what the list has always shown). */
const isUserActive = (u: DeviceUserRow) => u.statusId === 1;

const DeviceUser = () => {
  const preLanguage = localStorage.getItem('language');
  const { isMobile } = useResponsive();
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const listRef = useRef<ListScreenHandle>(null);
  const [language, setLanguage] = useState<any>(null);
  const companyId: string = localStorage.getItem('companyId') || '';
  const [showDelete, setShowDelete] = useState<boolean>(false);
  const [idDelete, setIdDelete] = useState<string>('');
  /** Optimistic desktop toggle state per user id; reset whenever the list refetches. */
  const [statusOverride, setStatusOverride] = useState<Record<string, boolean>>({});
  const inforTable: any = {
    img: <DeviceUserIcon />,
    title:
      language && language?.['No device users yet']
        ? language?.['No device users yet']
        : 'No device users yet',
    path: '/deviceUser/',
    desc:
      language && language?.['Assign and manage which users can access each POS device to ensure secure operations and maintain accountability for all transactions.']
        ? language?.['Assign and manage which users can access each POS device to ensure secure operations and maintain accountability for all transactions.']
        : 'Assign and manage which users can access each POS device to ensure secure operations and maintain accountability for all transactions.',
  };

  const handleKeyDown = (event: KeyboardEvent) => {
    if ((event.ctrlKey || event.metaKey) && event.key === 'f') {
      event.preventDefault();
      if (searchInputRef.current) {
        searchInputRef.current.focus();
      }
    }
  };

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []);
  useEffect(() => {
    if (window.languageContext) {
      setLanguage(window.languageContext.language);
    }
  }, [window, window.languageContext]);

  /** One page of device users; the search box maps onto the endpoint's `searchValue`. */
  const fetchDeviceUsers = async (query: ListQuery) => {
    webLoadingFalse();
    const response: DeviceUserResponse = await getListDeviceUser(
      companyId,
      query.page,
      query.limit,
      query.search
    );
    setStatusOverride({});
    return {
      rows: response.data.data.deviceUser ?? [],
      count: response.data.data.count ?? 0,
    };
  };

  const goToDeviceUser = (id: string) => webNavigate('/' + preLanguage + '/deviceUser/' + id);

  const handleShowDelete = (id: string) => {
    setShowDelete(true);
    setIdDelete(id);
  };
  const handleDeleteEmployee = async () => {
    try {
      const response = await deleteDeviceUser(idDelete);
      showDeleteToast(language?.['Device User Deleted !'] ?? 'Device User Deleted !');
    } catch (error) {
      console.error('Error loading terms:', error);
      showApiError(error, { language });
    } finally {
      listRef.current?.reload();
      setShowDelete(false);
    }
  };

  /** The desktop status switch: same isActive flip the old table sent. */
  const handleUpdateStatus = async (user: DeviceUserRow, currentlyOn: boolean) => {
    // `currentlyOn` is what the switch shows (row value or the optimistic
    // override); the payload must flip THAT, or a second click before the
    // list refetches would resend the first click's value.
    setStatusOverride(prev => ({ ...prev, [user.id]: !currentlyOn }));
    try {
      const payload: any = { ...user, isActive: !currentlyOn };
      const response = await updateDeviceUser(user.id, payload);
    } catch (error) {
      console.error('Error loading terms:', error);
      setStatusOverride(prev => ({ ...prev, [user.id]: currentlyOn }));
      showApiError(error, { language });
    } finally {
    }
  };

  /* ---------------- List kit configuration ---------------- */

  const columns: ListColumn<DeviceUserRow>[] = [
    {
      key: 'loginId',
      label: 'Login ID',
      width: 160,
      hideable: false,
      render: cells.primary(u => u.loginId),
      mobile: 'title',
      onClick: u => goToDeviceUser(String(u.id)),
    },
    { key: 'firstName', label: 'First Name', width: 160, mobile: 'subtitle' },
    { key: 'lastName', label: 'Last Name', width: 160, mobile: 'subtitle' },
    {
      key: 'role',
      label: 'Role',
      width: 140,
      minWidth: 96,
      value: u => u.role?.roleName ?? '',
      mobile: 'sheet',
    },
    {
      key: 'updatedAt',
      label: 'Last Update',
      width: 130,
      minWidth: 92,
      render: cells.date(u => u.updatedAt),
      mobile: 'sheet',
    },
    {
      key: 'status',
      label: 'Status',
      width: 100,
      value: (u, ctx) =>
        isUserActive(u)
          ? t(ctx.language, 'Active', 'Active')
          : t(ctx.language, 'Suspended', 'Suspended'),
      // Desktop keeps the toggle switch the old table rendered for /deviceUser/.
      render: u => {
        const on = statusOverride[u.id] ?? isUserActive(u);
        return (
          <div className="flex items-center gap-x-3">
            <div className={`switch ${on ? 'on' : 'off'}`} onClick={() => handleUpdateStatus(u, on)}>
              <div className={`shadow_toggle slider ${on ? 'move-right' : 'move-left'}`}></div>
            </div>
          </div>
        );
      },
      // Mobile shows a tag, as the old mobile table did.
      mobileRender: (u, ctx) => (
        <Pill
          label={
            isUserActive(u)
              ? t(ctx.language, 'Active', 'Active')
              : t(ctx.language, 'Suspended', 'Suspended')
          }
          kind={isUserActive(u) ? 'ok' : 'off'}
        />
      ),
      mobile: 'trailing',
    },
  ];

  const rowActions: ListRowAction<DeviceUserRow>[] = [
    {
      key: 'edit',
      label: 'Edit',
      icon: <EditIconV2 />,
      sheetIcon: 'inventory',
      onRun: u => goToDeviceUser(String(u.id)),
    },
    {
      key: 'delete',
      label: 'Delete',
      icon: <DeleteIconV2 />,
      sheetIcon: 'close',
      danger: true,
      onRun: u => handleShowDelete(String(u.id)),
    },
  ];

  return (
    <>
      <div
        className="h-full relative"
        style={{
          overflowY: isMobile ? 'auto' : undefined,
          paddingBottom: isMobile ? '24px' : undefined,
          display: isMobile ? undefined : 'flex',
          flexDirection: isMobile ? undefined : 'column',
        }}
      >
        <div className="lg:flex items-center justify-between" >
          <p
            style={{ color: '#222525' }}
            className="text-[24px] leading-[130%] font-medium font-inter"
          >
            {language && language?.['Device Users']
              ? language?.['Device Users']
              : 'Device Users'}
          </p>
          <div
            className="cursor-pointer flex items-center justify-center"
            style={{
              gap: '8px',
              padding: "8px 24px 8px 16px",
              backgroundColor: '#0F7679',
              borderRadius: '30px',
              width: "fit-content", marginTop: isMobile ? "20px" : '0px',
            }}
            onClick={() => {
              webNavigate('/' + preLanguage + '/deviceUser/new');
            }}
          >
            <div style={{ width: '24px', height: '24px' }} className="flex items-center justify-center">
              <PlusIconWhite />
            </div>
            <p
              style={{ color: '#FFFFFF' }}
              className="text-[14px] leading-[130%] font-medium font-inter"
            >
              {language && language?.['Add device user']
                ? language?.['Add device user']
                : 'Add device user'}
            </p>
          </div>
        </div>
        <ListScreen
          ref={listRef}
          language={language}
          columns={columns}
          fetchRows={fetchDeviceUsers}
          searchPlaceholder="Search device users"
          searchInputRef={searchInputRef}
          rowActions={rowActions}
          countNoun="device users"
          emptyState={inforTable}
        />
      </div>
      <div
        className={`fixed inset-0 flex items-center justify-center px-4 ${showDelete ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
          } transition-opacity duration-300 ease-in-out`}
        style={{ backgroundColor: "#22252552", zIndex: "200", transition: "opacity 0.3s ease, visibility 0.3s ease", width: "100%" }}
      >
        <div
          className={`transform transition-transform duration-300 ease-in-out ${showDelete ? "translate-y-0" : "translate-y-4 opacity-0"}`} style={{
            padding: "24px", borderRadius: "24px", backgroundColor: "#FFFFFF", width: "530px", maxWidth: "calc(100vw - 32px)", transition: "transform 0.3s ease, opacity 0.3s ease",
          }}
        >
          <div
            className="flex items-center justify-between"
          >
            <p style={{ fontSize: '20px' }} className="font-semibold font-inter text-main leading-[24px]">
              {language && language?.['Delete device user']
                ? language?.['Delete device user']
                : 'Delete device user'}
            </p>
            <div
              onClick={() => setShowDelete(false)}
              className="flex items-center justify-center cursor-pointer"
              style={{
                width: '24px',
                height: '24px',
              }}
            >
              <CloseIcon />
            </div>
          </div>
          <div className="flex items-center justify-between" >
            <p className="text-[14px] font-inter leading-[24px]" style={{ color: '#6A7878' }}>
              {language && language?.['This device user will be permanently deleted. Are you sure you want to continue?']
                ? language?.['This device user will be permanently deleted. Are you sure you want to continue?']
                : 'This device user will be permanently deleted. Are you sure you want to continue?'}
            </p>
            {/* <span className="text-main">[sale ID]</span> sale. */}
          </div>
          <div className="flex items-center justify-end" style={{ marginTop: '24px', gap: '12px' }}>
            <div
              onClick={() => setShowDelete(false)}
              className="cursor-pointer text-[14px] leading-[22px] font-inter font-medium"
              style={{
                borderRadius: '30px',
                padding: '10px 24px',
                border: '1px solid #C9D7D8',
                color: '#222525',
              }}
            >
              {language && language?.['Cancel'] ? language?.['Cancel'] : 'Cancel'}
            </div>
            <div
              onClick={handleDeleteEmployee}
              className="cursor-pointer text-[14px] leading-[22px] font-inter font-medium"
              style={{
                borderRadius: '30px',
                padding: '10px 24px',
                backgroundColor: '#BA0E13',
                color: '#FFFFFF',
              }}
            >
              {language && language?.['Delete'] ? language?.['Delete'] : 'Delete'}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default DeviceUser;
